import type { Locale } from '@movmania/core'
import { fr } from './locales/fr.js'

export type Dictionary = typeof fr
export type Section = keyof Dictionary

/**
 * Le français est la seule langue livrée pour l'instant — décision produit.
 * Toute la machinerie (clés typées, interpolation, pluriel) reste en place :
 * ajouter l'anglais consistera à déposer un `en.ts` du même type et à
 * l'enregistrer ici, sans toucher au reste de l'app.
 */
export const dictionaries = { fr } as const

export const LOCALES: readonly Locale[] = ['fr'] as const

export const LOCALE_LABELS: Record<Locale, string> = {
  fr: 'Français',
  en: 'English',
}

export const DEFAULT_LOCALE: Locale = 'fr'

type Vars = Record<string, string | number>

/**
 * Traduction par chemin "section.clé", avec interpolation {{var}} et une
 * règle de pluriel simple (clé `_plural` si `count` !== 1).
 *
 * Volontairement minimal : pas de dépendance, et remplaçable par i18next le
 * jour où il faudra des règles de pluriel complexes ou du chargement paresseux.
 */
export function translate(locale: Locale, path: string, vars?: Vars): string {
  const [section, key] = path.split('.') as [Section, string]
  const dict = dictionaries[locale as 'fr'] ?? dictionaries.fr
  const bucket = dict[section] as Record<string, string> | undefined
  if (!bucket) return path

  let resolvedKey = key
  if (vars && typeof vars['count'] === 'number' && vars['count'] !== 1) {
    if (bucket[`${key}_plural`]) resolvedKey = `${key}_plural`
  }

  const template = bucket[resolvedKey] ?? bucket[key]
  if (template === undefined) return path

  if (!vars) return template
  return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  )
}

export { fr }
